import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Button,
  Alert,
  Spinner,
  Table,
  InputGroup,
  Form,
  Modal,
  Pagination
} from 'react-bootstrap';
import { FaSearch, FaArrowLeft, FaBoxOpen, FaUndo } from 'react-icons/fa';

// CSS for report pagination
const paginationStyle = {
  wrapper: {
    margin: '15px 0',
    padding: '8px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  pageCount: {
    color: '#6c757d',
    fontWeight: '500'
  },
  pagination: {
    marginBottom: '0'
  }
};

const ReportArchive = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredReports, setFilteredReports] = useState([]);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState(false);
  const [reportToUnarchive, setReportToUnarchive] = useState(null);
  
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsPerPage] = useState(10);

  useEffect(() => {
    const fetchArchivedReports = async () => {
      try {
        const res = await axios.get('/api/reports/archived');
        setReports(res.data);
        setFilteredReports(res.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching archived reports:", err);
        setError(`Failed to load archived reports: ${err.response?.data?.msg || err.message}`);
        setLoading(false);
      }
    };

    fetchArchivedReports();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredReports(reports);
    } else {
      const lowercaseSearchTerm = searchTerm.toLowerCase();
      
      const filtered = reports.filter(report => {
        // Handle potentially missing properties safely
        const patientName = report.patient?.name?.toLowerCase() || '';
        const patientId = report.patient?.patientId?.toLowerCase() || '';
        const doctorName = report.doctor?.name?.toLowerCase() || '';
        const diagnosisText = report.diagnosis?.toLowerCase() || '';
        
        return patientName.includes(lowercaseSearchTerm) ||
               patientId.includes(lowercaseSearchTerm) ||
               doctorName.includes(lowercaseSearchTerm) ||
               diagnosisText.includes(lowercaseSearchTerm);
      });
      
      setFilteredReports(filtered);
    }
  }, [searchTerm, reports]);

  // Get current reports for pagination
  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = filteredReports.slice(indexOfFirstReport, indexOfLastReport);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // When filtering changes, reset to first page
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredReports.length]);

  const handleUnarchiveClick = (report) => {
    setReportToUnarchive(report);
    setShowUnarchiveModal(true);
  };

  const handleConfirmUnarchive = async () => {
    if (!reportToUnarchive) return;
    
    try {
      await axios.put(`/api/reports/${reportToUnarchive.id}/unarchive`);
      
      // Remove report from state
      setReports(reports.filter(r => r.id !== reportToUnarchive.id));
      setShowUnarchiveModal(false);
      setReportToUnarchive(null);
    } catch (err) {
      console.error(err);
      setError('Failed to unarchive report');
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading archived reports...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        {error}. <Link to="/">Go back to dashboard</Link>
      </Alert>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Report Archive</h2>
          <p className="text-muted">Archived reports are hidden from regular views</p>
        </div>
        <Link to="/reports">
          <Button variant="secondary">
            <FaArrowLeft className="me-2" /> Back to Reports
          </Button>
        </Link>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <InputGroup>
            <Form.Control
              placeholder="Search by patient name, ID, doctor, or diagnosis..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
          </InputGroup>
        </Card.Body>
      </Card>

      {filteredReports.length > 0 ? (
        <>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Report ID</th>
                <th>Patient</th>
                <th>Doctor</th>
                <th>Date</th>
                <th>Diagnosis</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentReports.map(report => (
                <tr key={report.id}>
                  <td className="report-id">{report.reportId || 'N/A'}</td>
                  <td>
                    {report.patient ? (
                      <>
                        <Link to={`/patients/${report.patient.id}`} className="text-decoration-none">
                          {report.patient.name}
                        </Link>
                        <div className="text-muted small">ID: {report.patient.patientId}</div>
                      </>
                    ) : (
                      <span>Patient unavailable</span>
                    )}
                  </td>
                  <td>{report.doctor ? report.doctor.name : 'N/A'}</td>
                  <td>{report.date ? new Date(report.date).toLocaleDateString() : 'N/A'}</td>
                  <td>
                    {report.diagnosis && report.diagnosis.length > 50
                      ? report.diagnosis.substring(0, 50) + '...'
                      : report.diagnosis || 'No diagnosis'}
                  </td>
                  <td>
                    <Link to={`/reports/${report.id}`} className="btn btn-sm btn-info me-2">
                      View
                    </Link>
                    <Button 
                      variant="success" 
                      size="sm" 
                      onClick={() => handleUnarchiveClick(report)}
                    >
                      <FaUndo className="me-1" /> Unarchive
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          
          {/* Pagination */}
          {filteredReports.length > reportsPerPage && (
            <div className="d-flex flex-column align-items-center mt-3 mb-3" style={paginationStyle.wrapper}>
              <div className="text-center mb-2" style={paginationStyle.pageCount}>
                <small>
                  Showing {indexOfFirstReport + 1}-{Math.min(indexOfLastReport, filteredReports.length)} of {filteredReports.length} reports
                </small>
              </div>
              <Pagination style={paginationStyle.pagination}>
                <Pagination.Prev
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                />
                
                {Array.from({ length: Math.ceil(filteredReports.length / reportsPerPage) }).map((_, index) => (
                  <Pagination.Item
                    key={index + 1}
                    active={index + 1 === currentPage}
                    onClick={() => paginate(index + 1)}
                  >
                    {index + 1}
                  </Pagination.Item>
                ))}
                
                <Pagination.Next
                  onClick={() => setCurrentPage(prev => 
                    Math.min(prev + 1, Math.ceil(filteredReports.length / reportsPerPage))
                  )}
                  disabled={currentPage === Math.ceil(filteredReports.length / reportsPerPage)}
                />
              </Pagination>
            </div>
          )}
        </>
      ) : (
        <div className="text-center my-5">
          <FaBoxOpen size={40} className="text-muted mb-3" />
          <p>No archived reports found.</p>
          <Link to="/reports">
            <Button variant="secondary">
              <FaArrowLeft className="me-2" /> Back to Reports
            </Button>
          </Link>
        </div>
      )}

      {/* Unarchive Confirmation Modal */}
      <Modal show={showUnarchiveModal} onHide={() => setShowUnarchiveModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Unarchive</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to unarchive this report?
          {reportToUnarchive?.patient && (
            <p>Patient: <strong>{reportToUnarchive.patient.name}</strong></p>
          )}
          {reportToUnarchive?.date && (
            <p>Date: <strong>{new Date(reportToUnarchive.date).toLocaleDateString()}</strong></p>
          )}
          <p className="mt-2">
            This will make the report visible in the main reports list again.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUnarchiveModal(false)}>
            Cancel
          </Button>
          <Button variant="success" onClick={handleConfirmUnarchive}>
            Unarchive Report
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ReportArchive; 
